/**
 * School Years React Query Hooks
 * Coláiste Lurgan Management System - School Year data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// Query Keys
const QUERY_KEYS = {
  schoolYears: (filters) => ['schoolYears', filters],
  schoolYear: (id) => ['schoolYear', id],
};

// Query invalidation helpers - these need to be called within mutation callbacks
const invalidateQueries = (queryClient) => ({
  schoolYears: () => {
    queryClient.invalidateQueries({ queryKey: ['schoolYears'] });
  },
  schoolYear: (id) => {
    queryClient.invalidateQueries({ queryKey: ['schoolYear', id] });
  },
});

// Main hook for school years list with filtering and search
export function useSchoolYears(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.schoolYears(params),
    queryFn: () => api.schoolYears.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

// Hook for single school year
export function useSchoolYear(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.schoolYear(id),
    queryFn: () => api.schoolYears.getById(id),
    enabled: !!id, // Only run if id is provided
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options,
  });
}

// Create school year mutation
export function useCreateSchoolYear(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.schoolYears.create,
    onSuccess: () => {
      invalidateQueries(queryClient).schoolYears();
    },
    ...options,
  });
}

// Update school year mutation
export function useUpdateSchoolYear(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.schoolYears.update(id, data),
    onSuccess: (data, variables) => {
      invalidateQueries(queryClient).schoolYears();
      invalidateQueries(queryClient).schoolYear(variables.id);
    },
    ...options,
  });
}

// Delete school year mutation
export function useDeleteSchoolYear(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.schoolYears.delete,
    onSuccess: () => {
      invalidateQueries(queryClient).schoolYears();
    },
    ...options,
  });
}

// Hook for active school years (commonly used for dropdowns)
export function useActiveSchoolYears(options = {}) {
  return useSchoolYears({ is_active: true }, {
    select: (data) => {
      // Handle both array and paginated response formats
      const schoolYears = Array.isArray(data) ? data : (data?.results || []);
      return schoolYears.filter(year => year.is_active);
    },
    ...options,
  });
}

// Hook for school year analytics (if needed in the future)
export function useSchoolYearAnalytics(options = {}) {
  return useQuery({
    queryKey: ['schoolYearAnalytics'],
    queryFn: () => api.schoolYears.getAnalytics?.() || Promise.resolve({}),
    staleTime: 10 * 60 * 1000, // 10 minutes for analytics
    enabled: !!api.schoolYears.getAnalytics, // Only if endpoint exists
    ...options,
  });
}

// Hook for bulk operations (if needed)
export function useBulkUpdateSchoolYears(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.schoolYears.bulkUpdate || (() => Promise.reject('Not implemented')),
    onSuccess: () => {
      invalidateQueries(queryClient).schoolYears();
    },
    ...options,
  });
}